import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';

// Components
import Navbar from './components/Navbar';
import Login from './components/Login';
import Mt5Login from './components/Mt5Login';
import ConfigInput from './components/ConfigInput';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Subscription from './components/Subscription';
import Homepage from './components/Homepage';
import Dashboard from './components/Dashboard';
import BrandLogo from './components/BrandLogo';
import InstallPrompt from './components/InstallPrompt';

// Pages
import About from './pages/About';
import Checkout from './pages/Checkout';
import Pricing from './pages/Pricing';
import Contact from './pages/Contact';
import Reviews from './pages/Reviews';
import Terms from './pages/Terms';
import VerifyEmail from './pages/VerifyEmail';

import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

const footerGroups = [
  {
    title: 'Platform',
    links: [
      { to: '/pricing', label: 'Pricing' },
      { to: '/reviews', label: 'Reviews' },
    ],
  },
  {
    title: 'Account',
    links: [
      { to: '/register', label: 'Start Free Trial' },
      { to: '/login', label: 'Login' },
      { to: '/forgot-password', label: 'Reset Password' },
    ],
  },
  {
    title: 'Company',
    links: [
      { to: '/about', label: 'About' },
      { to: '/contact', label: 'Contact' },
      { to: '/reviews', label: 'Reviews' },
    ],
  },
];

function ScrollManager() {
  const location = useLocation();

  React.useEffect(() => {
    if (location.hash) {
      const elementId = location.hash.slice(1);
      const scrollToElement = () => {
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return true;
        }

        return false;
      };

      if (scrollToElement()) {
        return undefined;
      }

      const timeoutId = window.setTimeout(scrollToElement, 150);
      return () => window.clearTimeout(timeoutId);
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    return undefined;
  }, [location.pathname, location.hash]);

  return null;
}

function AppShell() {
  const location = useLocation();
  const appShellPaths = ['/account', '/dashboard', '/config', '/mt5-login', '/subscription'];
  const isAppShellRoute = appShellPaths.includes(location.pathname);

  return (
    <div className="min-h-screen bg-[#05080c] text-white">
      {!isAppShellRoute && <Navbar />}
      <main className={isAppShellRoute ? '' : 'pt-24'}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route element={<ProtectedRoute requireSubscription={true} />}>
            <Route path="/account" element={<Dashboard />} />
            <Route path="/dashboard" element={<Navigate to="/account" replace />} />
            <Route path="/config" element={<ConfigInput />} />
          </Route>
          <Route element={<ProtectedRoute requireSubscription={false} />}>
            <Route path="/mt5-login" element={<Mt5Login />} />
            <Route path="/subscription" element={<Subscription />} />
          </Route>
          <Route path="/" element={<Homepage />} />
          <Route path="/about" element={<About />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {!isAppShellRoute && (
        <footer className="relative overflow-hidden border-t border-white/10 bg-[#07111a]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(27,186,123,0.12),_transparent_25%),radial-gradient(circle_at_80%_20%,_rgba(45,122,255,0.12),_transparent_30%)] opacity-80" />
          <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="grid gap-10 rounded-[2rem] border border-white/10 bg-white/5 p-8 backdrop-blur-xl lg:grid-cols-[1.15fr_0.85fr] lg:p-10">
              <div>
                <BrandLogo size="default" />

                <h2 className="mt-8 max-w-2xl text-3xl font-semibold text-white sm:text-4xl">
                  Structured MT5 automation with visible controls.
                </h2>
                <p className="mt-4 max-w-2xl text-base leading-8 text-slate-300 sm:text-lg">
                  Connect the account, set limits, and monitor execution from one account workspace.
                </p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/register"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-emerald-300 to-cyan-300 px-6 py-3 font-bold text-slate-950 transition hover:scale-[1.02]"
                  >
                    Start free trial
                    <FiArrowRight className="h-4 w-4" />
                  </Link>
                  <Link
                    to="/pricing"
                    className="inline-flex items-center justify-center rounded-full border border-white/12 bg-white/5 px-6 py-3 font-semibold text-white transition hover:bg-white/10"
                  >
                    Explore plans
                  </Link>
                </div>
              </div>

              <div className="grid gap-8 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
                {footerGroups.map((group) => (
                  <div key={group.title}>
                    <h3 className="text-sm font-semibold uppercase tracking-[0.24em] text-cyan-200/70">
                      {group.title}
                    </h3>
                    <div className="mt-4 space-y-3">
                      {group.links.map((link) => (
                        <Link
                          key={link.to}
                          to={link.to}
                          className="block text-sm text-slate-300 transition hover:text-white"
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3 border-t border-white/10 pt-6 text-sm text-slate-400 sm:flex-row sm:items-center sm:justify-between">
              <p>Built for traders who want clarity, control, and fewer distractions.</p>
              <p>© 2026 DailyProfit Trader. All rights reserved.</p>
            </div>
          </div>
        </footer>
      )}
      <InstallPrompt />
    </div>
  );
}

function App() {
  // Theme setup (unchanged)
  React.useEffect(() => {
    document.documentElement.classList.add('dark');
    document.documentElement.style.setProperty('--bg-primary', '#000000');
    document.documentElement.style.setProperty('--bg-secondary', '#121212');
    document.documentElement.style.setProperty('--bg-surface', '#1E1E1E');
    document.documentElement.style.setProperty('--text-primary', '#FFFFFF');
    document.documentElement.style.setProperty('--text-secondary', '#E0E0E0');
    document.documentElement.style.setProperty('--border-color', '#333333');
    document.documentElement.style.setProperty('--primary-color', '#00A878');
    document.documentElement.style.setProperty('--primary-hover', '#00c08b');
    document.documentElement.style.setProperty('--primary-dark', '#008060');
  }, []);

  return (
    <Router>
      <AuthProvider>
        <ScrollManager />
        <AppShell />
      </AuthProvider>
    </Router>
  );
}

export default App;